/**
 * Import et modèle CSV pour la saisie en masse.
 *
 * Le parsing est strict et *non destructif* : il produit un rapport, jamais
 * une écriture. C'est l'appelant qui décide d'écrire, après avoir montré ce
 * rapport. Vérifier qu'un fichier « vient du modèle » ne prouve rien — une
 * en-tête se recopie — ce contrôle sert le confort, pas la sécurité ; celle-ci
 * tient au fait que tout repasse par l'API, avec sa validation de schéma et
 * les contraintes SQL, sur le compte de l'utilisateur lui-même.
 */

import { AHEAD_DAYS, addDays, toISODay } from './stats.js';

export interface CsvRow {
  day: string;
  weight: number | null;
  steps: number | null;
  kcal: number | null;
  /** Séance de la veille : `null` quand la colonne est vide. */
  gym: boolean | null;
}

export interface CsvProblem {
  /** Numéro de ligne dans le fichier, en-tête comprise : ce que voit l'éditeur. */
  line: number;
  reason: string;
}

export interface CsvReport {
  rows: CsvRow[];
  problems: CsvProblem[];
  /** Lignes syntaxiquement valides mais sans aucune mesure : ignorées en silence. */
  empty: number;
}

/** Au-delà, ce n'est plus un carnet de suivi. Borne le temps de parsing. */
export const MAX_BYTES = 1024 * 1024;
export const MAX_ROWS = 2000;

/** Colonnes attendues, dans cet ordre. */
const HEADER = ['date', 'poids_kg', 'pas', 'calories', 'seance'] as const;

/** Marque d'octets UTF-8, en échappement pour rester visible à la lecture. */
const BOM = '\ufeff';
/** Fins de ligne Windows : c'est ce qu'attend un tableur. */
const EOL = '\r\n';

/**
 * La colonne séance accepte ce qu'un tableur est susceptible d'y trouver. Vide
 * vaut « non renseigné », ce qui n'est pas « pas de séance ».
 */
const TRUE_WORDS = new Set(['1', 'oui', 'o', 'vrai', 'v', 'true', 'x', 'yes', 'y']);
const FALSE_WORDS = new Set(['0', 'non', 'n', 'faux', 'f', 'false']);

function parseFlag(text: string): boolean | null | 'invalid' {
  const cleaned = text.trim().toLowerCase();
  if (cleaned === '') return null;
  if (TRUE_WORDS.has(cleaned)) return true;
  if (FALSE_WORDS.has(cleaned)) return false;
  return 'invalid';
}

/**
 * Bornes de plausibilité, identiques à celles du panneau de saisie. Elles ne
 * remplacent pas la validation serveur, elles évitent d'envoyer 300 lignes
 * pour s'en voir refuser une.
 */
const LIMITS = {
  weight: { min: 20, max: 400, label: 'poids hors de 20–400 kg' },
  steps: { min: 0, max: 100_000, label: 'pas hors de 0–100 000' },
  kcal: { min: 0, max: 20_000, label: 'calories hors de 0–20 000' },
} as const;

/**
 * Modèle à remplir. Point-virgule et virgule décimale : c'est ce qu'attend
 * Excel en configuration française, où un fichier séparé par des virgules
 * atterrit tout entier dans la colonne A. Le BOM lui fait lire de l'UTF-8.
 */
export function templateCsv(today = new Date()): string {
  const day = (offset: number) => {
    const date = new Date(today);
    date.setDate(date.getDate() + offset);
    return toISODay(date);
  };
  return [
    BOM + HEADER.join(';'),
    `${day(-2)};80,4;10432;2350;oui`,
    `${day(-1)};80,2;8710;2180;non`,
    `${day(0)};;;;`,
    '',
  ].join(EOL);
}

/**
 * Export dans le même dialecte que le modèle, donc relisible par ce parseur.
 * Aucune valeur ne peut commencer par `=`, `+` ou `@` — ce sont des dates ISO
 * et des nombres positifs : l'échappement contre l'interprétation en formule
 * par un tableur n'a pas lieu d'être ici.
 */
export function toCsv(rows: CsvRow[]): string {
  const lines = rows.map((row) =>
    [
      row.day,
      row.weight == null ? '' : String(row.weight).replace('.', ','),
      row.steps == null ? '' : String(row.steps),
      row.kcal == null ? '' : String(row.kcal),
      row.gym == null ? '' : row.gym ? 'oui' : 'non',
    ].join(';'),
  );
  return [BOM + HEADER.join(';'), ...lines, ''].join(EOL);
}

/** Sépare une ligne en respectant les guillemets, qu'Excel ajoute parfois. */
function splitLine(line: string, separator: string): string[] {
  const cells: string[] = [];
  let current = '';
  let quoted = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      // Deux guillemets consécutifs à l'intérieur d'un champ = un guillemet.
      if (quoted && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === separator && !quoted) {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells.map((cell) => cell.trim());
}

/**
 * Accepte `AAAA-MM-JJ` et `JJ/MM/AAAA` : taper la première dans Excel suffit à
 * la voir ressortir sous la seconde, et refuser le fichier qu'on vient soi-même
 * de produire serait absurde.
 */
function parseDay(text: string): string | null {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  const french = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);

  let year: number;
  let month: number;
  let day: number;
  if (iso) [, year, month, day] = iso.map(Number) as [number, number, number, number];
  else if (french) [, day, month, year] = french.map(Number) as [number, number, number, number];
  else return null;

  const date = new Date(year, month - 1, day);
  // Rejette le 31 février, que `Date` reporterait silencieusement au 3 mars.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return toISODay(date);
}

/** `1 234,5` comme `1234.5`. Une cellule vide vaut « mesure absente ». */
function parseNumber(text: string): number | null | 'invalid' {
  const cleaned = text.replace(/\s/g, '').replace(/,/g, '.');
  if (cleaned === '') return null;
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : 'invalid';
}

/**
 * Lit le contenu d'un fichier et rend un rapport. Ne lève que si le fichier
 * n'a rien d'un CSV exploitable — dans tous les autres cas, les erreurs sont
 * localisées à la ligne pour que l'utilisateur puisse corriger son fichier.
 */
export function parseCsv(content: string): CsvReport {
  const text = content.replace(/^\ufeff/, '');
  const lines = text.split(/\r\n|\n|\r/).filter((line) => line.trim() !== '');
  if (lines.length === 0) throw new Error('Fichier vide.');

  const header = lines[0] ?? '';
  // Le séparateur se déduit de l'en-tête, et fixe du même coup la
  // signification de la virgule dans les nombres.
  const separator = header.includes(';') ? ';' : header.includes('\t') ? '\t' : ',';
  const columns = splitLine(header, separator).map((cell) => cell.toLowerCase());

  // `seance` est arrivée après coup : un fichier produit avant reste lisible,
  // sa colonne absente étant lue comme « non renseigné ».
  const missing = HEADER.filter((name) => name !== 'seance' && !columns.includes(name));
  if (missing.length > 0) {
    throw new Error(
      `En-tête inattendue. Colonnes manquantes : ${missing.join(', ')}. Téléchargez le modèle.`,
    );
  }

  const at = Object.fromEntries(HEADER.map((name) => [name, columns.indexOf(name)])) as Record<
    (typeof HEADER)[number],
    number
  >;

  const body = lines.slice(1);
  if (body.length > MAX_ROWS) {
    throw new Error(`${body.length} lignes : le maximum accepté est de ${MAX_ROWS}.`);
  }

  // Une date qui revient deux fois : la dernière occurrence l'emporte, comme
  // une seconde saisie manuelle du même jour.
  const byDay = new Map<string, CsvRow>();
  const problems: CsvProblem[] = [];
  // Les journees a venir ouvertes a la saisie restent acceptables : l'export les
  // sort comme les autres, et un plan de repas peut arriver par la. Au-dela,
  // personne ne les verrait.
  const limit = toISODay(addDays(new Date(), AHEAD_DAYS));
  let empty = 0;

  body.forEach((line, offset) => {
    const number = offset + 2;
    const cells = splitLine(line, separator);

    const day = parseDay(cells[at.date] ?? '');
    if (!day) {
      problems.push({ line: number, reason: `date illisible « ${cells[at.date] ?? ''} »` });
      return;
    }
    if (day > limit) {
      problems.push({ line: number, reason: `date trop loin dans le futur (${day})` });
      return;
    }

    const flag = parseFlag(cells[at.seance] ?? '');
    if (flag === 'invalid') {
      problems.push({ line: number, reason: `séance illisible « ${cells[at.seance] ?? ''} »` });
      return;
    }

    const parsed: Record<'weight' | 'steps' | 'kcal', number | null> = {
      weight: null,
      steps: null,
      kcal: null,
    };
    const sources = { weight: at.poids_kg, steps: at.pas, kcal: at.calories } as const;

    let rejected = false;
    for (const field of ['weight', 'steps', 'kcal'] as const) {
      const value = parseNumber(cells[sources[field]] ?? '');
      if (value === 'invalid') {
        problems.push({ line: number, reason: `${field === 'weight' ? 'poids' : field === 'steps' ? 'pas' : 'calories'} illisible` });
        rejected = true;
        break;
      }
      if (value != null) {
        const bound = LIMITS[field];
        if (value < bound.min || value > bound.max) {
          problems.push({ line: number, reason: bound.label });
          rejected = true;
          break;
        }
      }
      // Pas et calories sont des entiers en base : on arrondit plutôt que de
      // rejeter un tableur qui a écrit « 10432,0 ».
      parsed[field] = value == null ? null : field === 'weight' ? value : Math.round(value);
    }
    if (rejected) return;

    // « séance = non » seul ne décrit rien : c'est l'absence d'information, pas
    // une mesure. Seul un « oui » justifie une ligne à lui tout seul.
    if (parsed.weight == null && parsed.steps == null && parsed.kcal == null && flag !== true) {
      empty += 1;
      return;
    }

    byDay.set(day, { day, ...parsed, gym: flag });
  });

  const rows = [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
  return { rows, problems, empty };
}
